import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { formatAuditTime } from '../services/auditTime';
import { serviceTitanJobUrl } from './serviceTitanJob';
import {
  ADDITIONAL_KEY,
  buildCategories,
  categoryDot,
  categoryStatusLabel,
  checklistCounts,
  correctionSummary,
  defaultCategoryKey,
  enteredValue,
  entryBadge,
  humanize,
  isPhotoFinding,
  matchesFieldFilter,
  nextStep,
  pointsLabel,
  whyFlagged,
} from './auditReviewView';
import {
  apiMessage,
  attachmentUrl,
  fieldPreviewUrl,
  FINDING_STATUSES,
  getAuditRun,
  saveFindingReview,
  SEVERITIES,
} from '../services/jobAuditApi';

const statusTone = (status) => {
  if (status === 'critical_failure' || status === 'fail' || status === 'missing' || status === 'invalid') {
    return 'bg-red-100 text-red-800';
  }
  if (status === 'human_review_required' || status === 'pending') {
    return 'bg-purple-100 text-purple-800';
  }
  if (status === 'technician_correction_required' || status === 'warning' || status === 'attention') {
    return 'bg-amber-100 text-amber-900';
  }
  if (status === 'pass' || status === 'accepted' || status === 'entered' || status === 'high') {
    return 'bg-green-100 text-green-800';
  }
  if (status === 'not_applicable' || status === 'na' || status === 'optional') return 'bg-gray-100 text-gray-600';
  return 'bg-gray-100 text-gray-700';
};

const fieldClass =
  'mt-1 block w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm shadow-sm';
const OBSERVED_PREVIEW = 240;

const ObservedText = ({ value }) => {
  const [expanded, setExpanded] = useState(false);
  const text = value || '';
  if (!text) return '—';
  if (expanded || text.length <= OBSERVED_PREVIEW) return text;
  return (
    <>
      {text.slice(0, OBSERVED_PREVIEW)}
      <button
        type="button"
        className="text-blue-700 hover:underline"
        aria-label="Show full observation"
        onClick={() => setExpanded(true)}
      >
        ...
      </button>
    </>
  );
};

const PARKED_HHRC_IDS = new Set(['signed_estimate', 'invoice_signature']);

const emptyDraft = (finding) => ({
  verdict: finding.review?.verdict || '',
  rationale: finding.review?.rationale || '',
  expected_severity: finding.review?.expected_severity || '',
  expected_status: finding.review?.expected_status || '',
  expected_reason_code: finding.review?.expected_reason_code || '',
  override_expected: finding.review?.override_expected || '',
  override_observed: finding.review?.override_observed || '',
  image_subject: finding.review?.image_subject || '',
  suitable_for_eval: Boolean(finding.review?.suitable_for_eval),
  hint: '',
});

const FindingReview = ({ finding, auditRunId, reviewVerdicts }) => {
  const [draft, setDraft] = useState(() => emptyDraft(finding));
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    const next = emptyDraft(finding);
    setDraft(next);
    draftRef.current = next;
  }, [finding]);

  const persist = async (next) => {
    if (!next.verdict) {
      setDraft((current) => ({ ...current, hint: 'Select a verdict to save.' }));
      return;
    }
    setDraft((current) => ({ ...current, hint: 'Saving…' }));
    try {
      const saved = await saveFindingReview(finding.finding_id, {
        audit_run_id: auditRunId,
        verdict: next.verdict,
        rationale: next.rationale,
        expected_severity: next.expected_severity || null,
        expected_status: next.expected_status || null,
        expected_reason_code: next.expected_reason_code || null,
        override_expected: next.override_expected || null,
        override_observed: next.override_observed || null,
        image_subject: next.image_subject || null,
        suitable_for_eval: next.suitable_for_eval,
      });
      setDraft((current) => ({ ...current, hint: `Saved ${saved.verdict}` }));
    } catch (err) {
      setDraft((current) => ({ ...current, hint: apiMessage(err, 'Save failed') }));
    }
  };

  const patch = (partial, { save } = {}) => {
    setDraft((current) => {
      const next = { ...current, ...partial };
      draftRef.current = next;
      if (save) window.setTimeout(() => persist(next), 0);
      return next;
    });
  };

  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-2">
      <label className="block text-xs text-gray-600">
        Verdict
        <select
          className={fieldClass}
          value={draft.verdict}
          onChange={(e) => patch({ verdict: e.target.value }, { save: true })}
        >
          <option value="">—</option>
          {reviewVerdicts.map((verdict) => (
            <option key={verdict} value={verdict}>
              {verdict}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs text-gray-600">
        Rationale
        <textarea
          className={fieldClass}
          rows={2}
          value={draft.rationale}
          onChange={(e) => patch({ rationale: e.target.value })}
          onBlur={() => persist(draftRef.current)}
        />
      </label>
      <label className="flex items-center gap-2 text-xs text-gray-600">
        <input
          type="checkbox"
          checked={draft.suitable_for_eval}
          onChange={(e) => patch({ suitable_for_eval: e.target.checked }, { save: true })}
        />
        Suitable for eval set
      </label>
      <label className="block text-xs text-gray-600">
        Expected severity
        <select
          className={fieldClass}
          value={draft.expected_severity}
          onChange={(e) => patch({ expected_severity: e.target.value }, { save: true })}
        >
          <option value="">(machine)</option>
          {SEVERITIES.map((severity) => (
            <option key={severity} value={severity}>
              {humanize(severity)}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs text-gray-600">
        Expected status
        <select
          className={fieldClass}
          value={draft.expected_status}
          onChange={(e) => patch({ expected_status: e.target.value }, { save: true })}
        >
          <option value="">(machine)</option>
          {FINDING_STATUSES.map((status) => (
            <option key={status} value={status}>
              {humanize(status)}
            </option>
          ))}
        </select>
      </label>
      {[
        ['expected_reason_code', 'Expected reason'],
        ['override_expected', 'Override expected'],
        ['override_observed', 'Override observed'],
        ['image_subject', 'Image subject'],
      ].map(([key, label]) => (
        <label key={key} className="block text-xs text-gray-600">
          {label}
          <input
            className={fieldClass}
            value={draft[key]}
            onChange={(e) => patch({ [key]: e.target.value })}
            onBlur={() => persist(draftRef.current)}
          />
        </label>
      ))}
      <div className="text-xs text-gray-500">{draft.hint}</div>
    </div>
  );
};

const Badge = ({ tone, children }) => (
  <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold ${statusTone(tone)}`}>
    {children}
  </span>
);

const AuditDetails = ({ finding, auditRunId, reviewVerdicts, showAdmin }) => {
  const reason = finding.reason_code || '';
  if (!reason && !finding.expected && !showAdmin) return null;
  return (
    <details className="mt-2 text-xs text-gray-500">
      <summary className="cursor-pointer text-blue-700">Audit details</summary>
      {reason ? <code className="mt-1 block break-words text-[11px]">{reason}</code> : null}
      {finding.expected ? <div className="mt-1">Expected: {finding.expected}</div> : null}
      {finding.field_id ? <div className="mt-1 font-mono">{finding.field_id}</div> : null}
      {showAdmin ? (
        <FindingReview finding={finding} auditRunId={auditRunId} reviewVerdicts={reviewVerdicts} />
      ) : null}
    </details>
  );
};

const Attention = ({ finding }) => {
  const next = nextStep(finding);
  const why = whyFlagged(finding);
  if (!next && !why) return <span className="text-gray-400">—</span>;
  return (
    <>
      {next ? <div className="text-sm text-gray-800">{next}</div> : null}
      {why ? <div className={next ? 'mt-1 text-xs text-gray-500' : 'text-sm text-gray-800'}>{why}</div> : null}
    </>
  );
};

const dotClass = {
  pass: 'bg-green-600',
  na: 'bg-gray-400',
  attention: 'bg-amber-600',
};

const JobAuditDetail = () => {
  const { auditRunId } = useParams();
  const { isAdmin } = useAuth();
  const showAdmin = isAdmin();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryKey, setCategoryKey] = useState('');
  const [fieldFilter, setFieldFilter] = useState('all');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAuditRun(auditRunId)
      .then((payload) => {
        if (!cancelled) setRun(payload);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(apiMessage(err, 'Failed to load audit run'));
          setRun(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [auditRunId]);

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setPreview(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  const auditFindings = useMemo(
    () => (run?.findings || []).filter((finding) => !PARKED_HHRC_IDS.has(finding.field_id)),
    [run]
  );
  const scoreItems = useMemo(
    () => (run?.score?.items || []).filter((item) => !PARKED_HHRC_IDS.has(item.item_id)),
    [run]
  );
  const categories = useMemo(
    () => buildCategories(scoreItems, auditFindings),
    [scoreItems, auditFindings]
  );
  const selected =
    categories.find((category) => category.key === categoryKey) ||
    categories.find((category) => category.key === defaultCategoryKey(categories)) ||
    null;

  useEffect(() => {
    if (!categories.length) return;
    if (!categories.some((category) => category.key === categoryKey)) {
      setCategoryKey(defaultCategoryKey(categories));
    }
  }, [categories, categoryKey]);

  if (loading) return <div className="text-sm text-gray-600">Loading audit…</div>;
  if (!run) {
    return (
      <div className="text-sm text-gray-600">
        Audit run not found.{' '}
        <Link className="text-blue-700 underline" to="/audit">
          Back to Audits
        </Link>
      </div>
    );
  }

  const score = run.score;
  const jobUrl = serviceTitanJobUrl(run.tenant_code, run.job_id);
  const counts = checklistCounts(scoreItems);
  const summary = correctionSummary(score?.issues);
  const place = run.tenant_location || run.tenant_code;
  const visible = (selected?.findings || []).filter((finding) => matchesFieldFilter(finding, fieldFilter));
  const tableRows = visible.filter((finding) => !isPhotoFinding(finding));
  const photos = visible.filter((finding) => isPhotoFinding(finding));
  const description = selected?.issueLabel
    ? selected.issueLabel
    : selected?.additional
      ? 'Findings outside the scored checklist.'
      : selected?.status === 'pass'
        ? 'This item passed.'
        : selected?.status === 'na'
          ? 'This item is not applicable to this job.'
          : '';

  const openPhoto = (src, title) => setPreview({ src, title });

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 text-gray-900">
      <div className="bg-white px-5 pt-4">
        <div className="mb-3 text-xs text-gray-500">
          <Link to="/audit" className="hover:underline">
            HHRC audits
          </Link>
          {place ? ` / ${place}` : ''} / Job {run.job_id}
        </div>
        <div className="flex items-center justify-between gap-5 pb-4">
          <div>
            <h1 className="text-[23px] font-semibold leading-tight">
              Job{' '}
              {jobUrl ? (
                <a href={jobUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                  {run.job_id}
                </a>
              ) : (
                run.job_id
              )}
            </h1>
            <p className="mt-1 text-sm">
              {run.technician || 'Technician'}
              {place ? <span className="text-xs text-gray-500"> · {place}</span> : null}
              {run.tenant_code && place !== run.tenant_code ? (
                <span className="text-xs text-gray-500"> {run.tenant_code}</span>
              ) : null}
            </p>
            {run.overall_job_status ? (
              <div className="mt-2">
                <Badge tone={run.overall_job_status}>{humanize(run.overall_job_status)}</Badge>
              </div>
            ) : null}
          </div>
          <div className="text-right">
            <div className="text-3xl font-semibold leading-none">
              {score?.score ?? '—'}
              <span className="text-sm font-normal text-gray-500"> / 100</span>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              {score?.earned_weight ?? 0} of {score?.applicable_weight ?? 0} applicable points
              <br />
              {counts.needsCorrection}{' '}
              {counts.needsCorrection === 1 ? 'item needs' : 'items need'} correction
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 border-t border-slate-200 py-2.5 text-xs text-gray-500">
          <span>
            Submitted <b className="font-normal text-gray-900">{formatAuditTime(run.created_at)}</b>
          </span>
          <span>
            Submission <b className="font-normal text-gray-900">{run.submission_id}</b>
          </span>
          {scoreItems.length > 0 ? (
            <span>
              {counts.passed} passed · {counts.notApplicable} not applicable
            </span>
          ) : null}
        </div>
      </div>

      <div className={`grid gap-4 p-4 ${scoreItems.length ? 'md:grid-cols-[235px_1fr]' : ''}`}>
        {scoreItems.length > 0 ? (
        <aside className="self-start rounded-lg border border-slate-200 bg-white px-2 py-3">
          {scoreItems.length > 0 ? (
            <div className="px-2 pb-2 text-[11px] uppercase tracking-wide text-gray-500">Scored checklist</div>
          ) : null}
          {categories
            .filter((category) => !category.additional)
            .map((category) => (
              <button
                key={category.key}
                type="button"
                aria-pressed={selected?.key === category.key}
                onClick={() => setCategoryKey(category.key)}
                className={`my-0.5 flex w-full items-center justify-between gap-2 rounded-md px-2 py-2.5 text-left text-xs ${
                  selected?.key === category.key ? 'bg-blue-50 text-blue-800' : 'text-gray-800'
                }`}
              >
                <span>
                  <span className={`mr-2 inline-block h-1.5 w-1.5 rounded-full ${dotClass[categoryDot(category)]}`} />
                  {category.name}
                </span>
                <span>›</span>
              </button>
            ))}
          {categories.some((category) => category.additional && category.key === ADDITIONAL_KEY && scoreItems.length > 0) ? (
            <>
              <div className="px-2 pb-2 pt-3 text-[11px] uppercase tracking-wide text-gray-500">
                Additional findings
              </div>
              {categories
                .filter((category) => category.additional)
                .map((category) => (
                  <button
                    key={category.key}
                    type="button"
                    aria-pressed={selected?.key === category.key}
                    onClick={() => setCategoryKey(category.key)}
                    className={`my-0.5 flex w-full items-center justify-between gap-2 rounded-md px-2 py-2.5 text-left text-xs ${
                      selected?.key === category.key ? 'bg-blue-50 text-blue-800' : 'text-gray-800'
                    }`}
                  >
                    <span>{category.name}</span>
                    <span>›</span>
                  </button>
                ))}
            </>
          ) : null}
        </aside>
        ) : null}

        <div className="min-w-0">
          {summary ? (
            <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 text-sm">
              <b>Correction summary</b>
              <br />
              {summary}
            </div>
          ) : null}
          {selected ? (
            <>
              <h2 className="text-[17px] font-semibold">{selected.name}</h2>
              {description ? <p className="mt-1 text-xs text-gray-500">{description}</p> : null}
              <div className="my-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-0.5 rounded-md bg-slate-200 p-0.5" aria-label="Field display">
                  {[
                    ['all', 'All fields'],
                    ['attention', 'Needs attention'],
                    ['entered', 'Entered'],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={fieldFilter === value}
                      onClick={() => setFieldFilter(value)}
                      className={`rounded px-2 py-1.5 text-xs ${
                        fieldFilter === value ? 'bg-white shadow-sm' : ''
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <Badge tone={selected.status === 'fail' ? 'attention' : selected.status === 'pass' ? 'entered' : 'optional'}>
                  {categoryStatusLabel(selected)} · {pointsLabel(selected)}
                </Badge>
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {tableRows.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-[11px] uppercase tracking-wide text-gray-500">
                          <th className="w-[28%] px-3.5 py-2.5 font-normal">Field</th>
                          <th className="w-[24%] px-3.5 py-2.5 font-normal">What was entered</th>
                          <th className="px-3.5 py-2.5 font-normal">What needs attention</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableRows.map((finding) => (
                          <tr key={finding.finding_id} className="border-t border-slate-200 align-top">
                            <td className="px-3.5 py-3.5 font-semibold">{finding.field_label || finding.field_id}</td>
                            <td className="px-3.5 py-3.5">
                              <span className="mb-1.5 block font-bold">
                                <ObservedText value={enteredValue(finding)} />
                              </span>
                              <Badge tone={fieldKindTone(finding)}>{entryBadge(finding)}</Badge>
                            </td>
                            <td className="px-3.5 py-3.5">
                              <Attention finding={finding} />
                              <AuditDetails
                                finding={finding}
                                auditRunId={run.audit_run_id}
                                reviewVerdicts={run.review_verdicts || []}
                                showAdmin={showAdmin}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {photos.length > 0 ? (
                  <div className="px-3.5">
                    {photos.map((finding) => (
                      <PhotoCard
                        key={finding.finding_id}
                        finding={finding}
                        auditRunId={run.audit_run_id}
                        reviewVerdicts={run.review_verdicts || []}
                        showAdmin={showAdmin}
                        onOpen={openPhoto}
                      />
                    ))}
                  </div>
                ) : null}
                {tableRows.length === 0 && photos.length === 0 ? (
                  <div className="px-6 py-6 text-sm text-gray-500">
                    {fieldFilter === 'all' ? 'No field values on this item.' : 'No matching fields.'}
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white px-6 py-6 text-sm text-gray-500">
              No findings on this audit.
            </div>
          )}
        </div>
      </div>

      {preview ? (
        <div
          className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/40 p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Uploaded photo preview"
        >
          <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-4">
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="float-right rounded border border-slate-200 px-2.5 py-1 text-sm"
            >
              Close
            </button>
            <h3 className="text-base font-semibold">{preview.title}</h3>
            <img src={preview.src} alt={preview.title} className="mx-auto mt-4 max-h-[70vh] max-w-full" />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const fieldKindTone = (finding) => {
  const badge = entryBadge(finding);
  if (badge === 'Invalid entry' || badge === 'Missing') return 'invalid';
  if (badge === 'Entered') return 'entered';
  return 'optional';
};

const PhotoCard = ({ finding, auditRunId, reviewVerdicts, showAdmin, onOpen }) => {
  const label = finding.field_label || finding.field_id;
  const flagged = entryBadge(finding) !== 'Entered' && entryBadge(finding) !== 'Optional blank';
  const thumbs = [
    ...(finding.evidence_attachment_ids || []).map((aid) => ({
      key: aid,
      src: attachmentUrl(auditRunId, aid),
      alt: aid,
    })),
    ...(finding.has_field_preview
      ? [{ key: 'preview', src: fieldPreviewUrl(auditRunId, finding.field_id), alt: 'Signature preview' }]
      : []),
  ];
  return (
    <div className="grid grid-cols-[83px_1fr] gap-3.5 border-t border-slate-200 py-4 first:border-t-0">
      <div>
        {thumbs.length ? (
          thumbs.map((thumb) => (
            <button
              key={thumb.key}
              type="button"
              className="mb-2 block h-20 w-20 overflow-hidden rounded-md border border-slate-200 bg-slate-50 p-0"
              aria-label={`Enlarge ${label} photo`}
              onClick={() => onOpen(thumb.src, label)}
            >
              <img src={thumb.src} alt={thumb.alt} className="h-full w-full object-cover" />
            </button>
          ))
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 px-1 text-center text-[11px] text-gray-500">
            {enteredValue(finding)}
          </div>
        )}
      </div>
      <div>
        <b className="mb-1 block">
          {label}{' '}
          <Badge tone={flagged ? 'attention' : 'entered'}>{flagged ? 'Uploaded · flagged' : 'Uploaded'}</Badge>
        </b>
        <div className="mt-1 text-sm text-gray-800">
          <Attention finding={finding} />
        </div>
        <AuditDetails
          finding={finding}
          auditRunId={auditRunId}
          reviewVerdicts={reviewVerdicts}
          showAdmin={showAdmin}
        />
      </div>
    </div>
  );
};

export default JobAuditDetail;
